import Vue from 'vue'
import UserCard from '@/components/UserCard'
import CompanyCard from '@/components/CompanyCard'
import Icon from '@/components/Icon'
import appConfig from '@/config.js'
import ShippingUuidFilter from '@/common/shippingUuid.filter'
import CurrencyFilter from '@/common/currency.filter'
import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
import AllInOneGeoAutocomplete from '@/components/autocompletes/AllInOneGeoAutocomplete'
import addressMixin from './address.mixin'
import FieldQuoteFormula from '@/components/FieldQuoteFormula'

export default {
  mixins: [addressMixin],
  computed: {
    models() {
      return {
        announcements: {
          title: this.$gettext('Announcements'),
          endpoint: 'announcements',
          allowNew: true,
          shared: false,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Country ISO code'),
              type: 'select',
              options: 'countries',
              showKey: 'iso_code',
              showField: 'name',
              withSelect: true,
              default: window.country || null
            },
            {
              key: 'app',
              label: this.$gettext('App'),
              sortable: false,
              editable: true,
              required: true,
              type: 'select',
              options: [
                {
                  value: 'dashboard',
                  text: this.$gettext('Dashboard')
                },
                {
                  value: 'dashboard_external',
                  text: this.$gettext('External Tracking Page')
                }
              ],
              withSelect: true,
              default: null,
              description: this.$gettext('Client app where the announcement must be shown')
            },
            {
              key: 'date_from',
              label: this.$gettext('From Date'),
              sortable: true,
              editable: true,
              required: true,
              type: 'datetime',
              formatter: (value, key, item) => {
                const tz = window.countries.find(x => x.iso_code === item.country_iso_code).front_config.i18n.tz
                return window.moment.utc(value).tz(tz).format('L LT')
              },
              description: this.$gettext('Date since the announcement applies')
            },
            {
              key: 'date_to',
              label: this.$gettext('To Date'),
              sortable: true,
              editable: true,
              required: true,
              type: 'datetime',
              formatter: (value, key, item) => {
                const tz = window.countries.find(x => x.iso_code === item.country_iso_code).front_config.i18n.tz
                return window.moment.utc(value).tz(tz).format('L LT')
              },
              description: this.$gettext('Date until the announcement applies')
            },
            {
              key: 'message',
              label: this.$gettext('Message'),
              sortable: true,
              editable: true,
              required: true,
              type: 'textarea',
              description: this.$gettext('Announcement message')
            },
            {
              key: 'active',
              label: this.$gettext('Active'),
              sortable: true,
              editable: true,
              required: true,
              type: Boolean,
              description: this.$gettext('Indicates if the announcement is enabled'),
              default: 1
            }
          ],
          filters: ['id', 'country_iso_code', 'app', 'active']
        },
        invoices: {
          title: this.$gettext('Invoices'),
          endpoint: 'invoices',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'account_id',
              label: this.$gettext('Account ID'),
              sortable: true
            },
            {
              key: 'external_id',
              label: this.$gettext('External ID'),
              sortable: true
            },
            {
              key: 'type_id',
              label: this.$gettext('Type ID'),
              sortable: true
            },
            {
              key: 'gross_amount',
              label: this.$gettext('Gross Amount'),
              sortable: false
            },
            {
              key: 'invoice_url',
              label: this.$gettext('Invoice URL'),
              sortable: false,
              type: 'link'
            },
            {
              key: 'generation_date',
              label: this.$gettext('Generation Date'),
              sortable: true
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At'),
              sortable: false
            }
          ],
          includes: ['account_transactions'],
          filters: [
            'id',
            'account_id',
            'external_id',
            'generation_date',
            'account_transactions.shipping_id',
            'account_transactions.courier_id'
          ]
        },
        'accounting-catalogs': {
          title: this.$gettext('Accounting Catalogs'),
          endpoint: 'accounting-catalogs',
          allowNew: true,
          allowDelete: true,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'catalog',
              label: this.$gettext('Catalog'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext(
                '**MUST BE** one of the following values: payment_periods, invoicing_programs, customer_types, document_types, tax_conditions, transaction_types'
              )
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('The name of the catalog item')
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('A brief description of the catalog item')
            },
            {
              key: 'external_id',
              label: this.$gettext('External ID'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('ID for this catalog item into the ERP')
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country ISO Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Country (2 letter ISO code) to which this entry belongs to')
            }
          ],
          filters: ['id', 'catalog', 'name', 'external_id', 'country_iso_code']
        },
        'erp-hooks': {
          title: this.$gettext('ERP Hooks'),
          endpoint: 'erp-hooks',
          noIdLink: true,
          fields: [
            {
              key: 'id'
            },
            {
              key: 'target',
              label: this.$gettext('Target'),
              sortable: true
            },
            {
              key: 'operation',
              label: this.$gettext('Operation')
            },
            {
              key: 'entity',
              label: this.$gettext('Entity'),
              formatter: (value, key, item) => {
                let entity = item.model_type.split('\\')[item.model_type.split('\\').length - 1]
                let link = ''
                switch (entity) {
                  case 'Company':
                    link += '<a href="/edit/companies/' + item.model_id + '" target="_blank">'
                    break
                  case 'AccountTransaction':
                    link +=
                      '<a href=\'/transactions/?s=%5B%5B"id","eq","' + item.model_id + '"%5D%5D\' target="_blank">'
                    break
                  default:
                    link += '<a href="/edit/users/' + item.model_id + '" target="_blank">'
                    break
                }
                link += entity + ' #' + item.model_id + '</a>'
                return link
              }
            },
            {
              key: 'status',
              label: this.$gettext('Status')
            },
            {
              key: 'attempts',
              label: this.$gettext('Attempts'),
              sortable: true
            },
            {
              key: 'sync_error',
              label: this.$gettext('Sync Error')
            },
            {
              key: 'scheduled_date',
              label: this.$gettext('Scheduled Date')
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At')
            }
          ],
          extraActions: [
            {
              label: this.$gettext('Reschedule'),
              type: 'button',
              icon: 'redo',
              title: this.$gettext('Reschedule this ERP Hook'),
              variant: 'dark',
              position: 'end',
              disabledCondition: item => {
                return item.status !== 'canceled'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to RESCHEDULE this ERP Hook?'))) {
                  Vue.http.put(appConfig.api.baseUrl + 'erp-hooks/' + item.id + '/reschedule').then(response => {
                    window.moovaAdmin.refreshTable('apiListTable')
                  })
                }
              }
            },
            {
              label: this.$gettext('Discard'),
              type: 'button',
              icon: 'trash-alt',
              title: this.$gettext('Discard this ERP Hook'),
              variant: 'danger',
              position: 'end',
              disabledCondition: item => {
                return item.status !== 'pending'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to DISCARD this ERP Hook?'))) {
                  Vue.http.delete(appConfig.api.baseUrl + 'erp-hooks/' + item.id).then(response => {
                    window.moovaAdmin.refreshTable('apiListTable')
                  })
                }
              }
            }
          ],
          filters: ['id', 'target', 'operation', 'status', 'model_type', 'model_id', 'attempts']
        },
        // area_formulas: {
        //   title: this.$gettext('Area Formulas'),
        //   endpoint: 'area_formulas',
        //   fields: [
        //     {
        //       key: 'id',
        //       sortable: true
        //     },
        //     {
        //       key: 'area_id',
        //       label: this.$gettext('Area ID'),
        //       sortable: true,
        //       editable: true
        //     },
        //     {
        //       key: 'vehicle_type_id',
        //       label: this.$gettext('Vehicle Type'),
        //       type: 'Relationship',
        //       editable: true
        //     },
        //     {
        //       key: 'formula',
        //       label: this.$gettext('Formula'),
        //       editable: true
        //     },
        //     {
        //       key: 'courier_commission_formula',
        //       label: this.$gettext('Courier Commission Formula'),
        //       editable: true
        //     },
        //     {
        //       key: 'sort',
        //       label: this.$gettext('Sort'),
        //       type: Number,
        //       editable: true
        //     }
        //   ],
        //   filters: [
        //     'id',
        //     'area_id',
        //     'vehicle_type_id'
        //   ]
        // },
        regions: {
          title: this.$gettext('Regions'),
          endpoint: 'regions',
          allowNew: true,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country ISO Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Country (2 letter ISO code)')
            },
            {
              key: 'type',
              label: this.$gettext('Type'),
              editable: true,
              required: true,
              description: this.$gettext('Region type (ie. company_management)')
            }
          ],
          filters: ['id', 'name', 'country_iso_code', 'type'],
          includes: ['areas']
        },
        areas: {
          title: this.$gettext('Areas'),
          endpoint: 'areas',
          allowNew: true,
          countrize: 'country',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'country',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Country (2 letter ISO code)')
            },
            {
              key: 'area',
              label: this.$gettext('Area'),
              editable: false
            },
            {
              key: 'currency',
              label: this.$gettext('Currency'),
              editable: true,
              required: true,
              description: this.$gettext('Currency (3 letter ISO code)')
            },
            {
              key: 'symbol',
              label: this.$gettext('Symbol'),
              editable: true,
              required: true,
              description: this.$gettext('Currency symbol')
            },
            {
              key: 'locale',
              label: this.$gettext('Locale'),
              editable: true,
              required: true,
              description: this.$gettext('Language Locale (ISO 15897 code, ie. es_AR or pt_BR)')
            },
            {
              key: 'is_dangerous',
              label: this.$gettext('Is Dangerous?'),
              editable: true,
              required: true,
              description: this.$gettext('Boolean to define if the area is dangerous or not')
            }
          ],
          filters: ['id', 'name', 'country', 'area', 'is_dangerous'],
          includes: ['platform_areas', 'regions'],
          exclude: ['area']
        },
        'routing-zones': {
          title: this.$gettext('Routing Zones'),
          endpoint: 'routing-zones',
          allowNew: true,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'code',
              label: this.$gettext('Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Code to show in labels (max. 8 chars)')
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country ISO Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Country (2 letter ISO code)')
            },
            {
              key: 'area',
              label: this.$gettext('Area'),
              editable: false
            }
          ],
          filters: ['id', 'name', 'code', 'country_iso_code'],
          exclude: ['area']
        },
        'delivery-time-ranges': {
          title: this.$gettext('Delivery Time Ranges'),
          endpoint: 'delivery-time-ranges',
          allowNew: true,
          countrizeById: 'country_id',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('A name to identify the range like "morning" or "afternoon"')
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: false,
              editable: true,
              required: false,
              description: this.$gettext('Optional description for the time range')
            },
            {
              key: 'country_id',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              type: 'select',
              options: 'countriesById',
              showKey: 'id',
              showField: 'name',
              withSelect: true
            },
            {
              key: 'time_from',
              label: this.$gettext('Time From'),
              sortable: false,
              editable: true,
              required: true,
              description: this.$gettext('Time where this time range starts. Format hh:mm:ss'),
              type: 'time',
              step: 1,
              withSeconds: true
            },
            {
              key: 'time_to',
              label: this.$gettext('Time To'),
              sortable: false,
              editable: true,
              required: true,
              description: this.$gettext('Time where this time range ends. Format hh:mm:ss'),
              type: 'time',
              step: 1,
              withSeconds: true
            }
          ],
          filters: ['name', 'country_id']
        },
        'postal-codes': {
          title: this.$gettext('Postal Codes'),
          endpoint: 'postal-codes',
          allowNew: true,
          countrize: 'country',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'code',
              label: this.$gettext('Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Postal code')
            },
            {
              key: 'country',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Country ISO code'),
              type: 'select',
              options: 'countries',
              showKey: 'iso_code',
              showField: 'name',
              withSelect: true
            },
            {
              key: 'reference_address',
              label: this.$gettext('Reference Address'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Reference address for this postal code')
            },
            {
              key: 'reference_google_place_id',
              label: this.$gettext('Reference Google Place ID'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Google place ID for the reference address')
            }
          ],
          filters: ['code', 'country', 'reference_address']
        },
        'take-booster-configs': {
          title: this.$gettext('Take Booster Configs'),
          endpoint: 'take-booster-configs',
          allowNew: true,
          allowDelete: true,
          countrize: 'region.country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'reference',
              label: this.$gettext('Reference'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'region_id',
              label: this.$gettext('Region'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Region ID')
            },
            {
              key: 'type',
              label: this.$gettext('Type'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext(
                'Config type, wether this is for reducing or increasing the courier commission. Must be one of: <strong>boost</strong> or <strong>give</strong>.'
              )
            },
            {
              key: 'general_coefficient',
              label: this.$gettext('General Coefficient'),
              editable: true,
              description: this.$gettext('General coefficient factor (variable M in formula)')
            },
            {
              key: 'distance_coefficient',
              label: this.$gettext('Distance Coefficient'),
              editable: true,
              description: this.$gettext('Distance coefficient factor (variable D in formula)')
            },
            {
              key: 'stops_coefficient',
              label: this.$gettext('Stops Coefficient'),
              editable: true,
              description: this.$gettext('Stops coefficient factor (variable S in formula)')
            },
            {
              key: 'time_coefficient',
              label: this.$gettext('Time Coefficient'),
              editable: true,
              description: this.$gettext('Time coefficient factor (variable H in formula)')
            },
            {
              key: 'adjustment_constant',
              label: this.$gettext('Adjustment Constant'),
              editable: true,
              description: this.$gettext('Adjustment constant (variable C in formula)')
            },
            {
              key: 'apply_confirmed',
              label: this.$gettext('Apply to CONFIRMED'),
              editable: true,
              type: Boolean,
              description: this.$gettext('Indicates if this configs applies when tours are marked as confirmed')
            },
            {
              key: 'apply_waiting',
              label: this.$gettext('Apply to WAITING'),
              editable: true,
              type: Boolean,
              description: this.$gettext("Indicates if this configs applies when tours are send to 'waiting' list")
            },
            {
              key: 'time_from',
              label: this.$gettext('Time From'),
              editable: true,
              type: 'time',
              description: this.$gettext(
                'Within <code>time_to</code> configures a timming range, outside of it this time is the default. Must be lower than <code>time_to</code>'
              )
            },
            {
              key: 'time_to',
              label: this.$gettext('Time To'),
              editable: true,
              type: 'time',
              description: this.$gettext(
                'Time up to the rule applies considering current time, outside of the range is <code>time_to</code>. Must be higher than <code>time_from</code>'
              )
            },
            {
              key: 'enabled',
              label: this.$gettext('Enabled'),
              editable: true,
              type: Boolean
            }
          ],
          filters: ['id', 'reference', 'region_id', 'region.country_iso_code', 'enabled'],
          includes: ['region']
        },
        transactions: {
          title: this.$gettext('Transactions'),
          endpoint: 'accounts/transactions',
          allowNew: false,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'type',
              label: this.$gettext('Type'),
              sortable: true
            },
            {
              key: 'relationship',
              label: this.$gettext('Relationship'),
              sortable: false,
              editable: false,
              required: false
            },
            {
              key: 'account',
              label: this.$gettext('Account'),
              sortable: false,
              editable: false,
              required: false
            },
            {
              key: 'reason',
              label: this.$gettext('Reason'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'reference',
              label: this.$gettext('Reference'),
              editable: true,
              required: true
            },
            {
              key: 'account_owner',
              label: this.$gettext('Account Owner'),
              editable: false,
              required: false
            },
            {
              key: 'amount',
              label: this.$gettext('Amount'),
              editable: true,
              required: true
            },
            {
              key: 'shipping_id',
              label: this.$gettext('Related to'),
              sortable: true,
              editable: true,
              required: true,
              formatter: (value, key, item) => {
                let html = ''
                if (item.is_outsourcing) {
                  html += '<span class="badge badge-danger">3P</span>'
                }
                if (value) {
                  html +=
                    '<span class="badge badge-info">' +
                    this.$gettext('Shipping') +
                    '</span><br><a href="/edit/shippings/' +
                    value +
                    '" target="_blank">#' +
                    ShippingUuidFilter(value) +
                    '</a>'
                }
                if (item.accounting_note && item.accounting_note.service) {
                  if (value) {
                    html += '<br>'
                  }
                  html +=
                    '<span class="badge badge-info">' +
                    this.$gettext('Service') +
                    '</span><br>' +
                    item.accounting_note.service.name
                }
                if (item.fleet_rental_order_id) {
                  if (value) {
                    html += '<br>'
                  }
                  html +=
                    '<span class="badge badge-info">' +
                    this.$gettext('Fleet Rental Order') +
                    '</span><br><a href="/edit/fleet-rental-orders/' +
                    item.fleet_rental_order_id +
                    '" target="_blank">#' +
                    item.fleet_rental_order_id +
                    '</a>'
                }
                return html
              }
            },
            {
              key: 'applied_by_id',
              label: this.$gettext('Applied By'),
              sortable: true,
              editable: true,
              formatter: (value, key, item) => {
                return '<a href="/edit/users/' + value + '" target="_blank">#' + value + '</a>'
              }
            },
            {
              key: 'visibility',
              editable: true,
              required: true,
              label: this.$gettext('Take')
            },
            {
              key: 'imputation_date',
              label: this.$gettext('Imputation Date'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true,
              editable: true
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country'),
              editable: true,
              required: true,
              description: this.$gettext('Country ISO 3166-1 alpha 2 code (ie. AR or BR)')
            }
          ],
          filters: [
            'id',
            'name',
            'relationship',
            'type',
            'reason',
            'amount',
            'shipping_id',
            'fleet_rental_order_id',
            'country_iso_code'
          ],
          exclude: ['account', 'updated_at'],
          includes: ['account.owner', 'shipping', 'accounting_note.service']
        },
        'cod-payments': {
          title: this.$gettext('COD Payments'),
          endpoint: 'cod-payments',
          noIdLink: true,
          countrizeById: 'country_id',
          fields: [
            {
              key: 'id'
            },
            {
              key: 'platform_id',
              label: this.$gettext('Platform'),
              formatter: (value, key, item) => {
                return item.platform.name
              }
            },
            {
              key: 'user_id',
              label: this.$gettext('User'),
              formatter: (value, key, item) => {
                return new Vue({
                  ...UserCard,
                  parent: this,
                  propsData: {
                    user: item.user,
                    profilePicture: false,
                    withTitles: false,
                    withMap: false
                  }
                }).$mount().$el.outerHTML
              }
            },
            {
              key: 'external_transaction_id',
              label: this.$gettext('Ext. Trans. ID')
            },
            {
              key: 'amount',
              label: this.$gettext('Amount')
            },
            {
              key: 'deposited_amount',
              label: this.$gettext('Deposited Amount')
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              formatter: (value, key, item) => {
                let color = 'info'
                if (value === 'incidence') {
                  color = 'warning'
                } else if (value !== 'pending') {
                  color = 'success'
                }
                return (
                  '<span class="text-' +
                  color +
                  ' text-nowrap"><span style="display: inline-block; width:5px; height:5px; border-radius: 100px; vertical-align:middle;" class="bg-' +
                  color +
                  '"></span> ' +
                  value +
                  '</span>'
                )
              }
            },
            {
              key: 'reference',
              label: this.$gettext('Reference')
            },
            {
              key: 'transactions',
              label: this.$gettext('Transactions'),
              formatter: (value, key, item) => {
                return (
                  '<a href=/list/cod-transactions?s=%5B%5B"id","in",' +
                  JSON.stringify(value.map(x => x.id)) +
                  '%5D%5D>' +
                  this.$gettextInterpolate(this.$gettext('%{qty} transaction/s'), { qty: value.length }) +
                  '</a>'
                )
              }
            },
            {
              key: 'picture_url',
              label: this.$gettext('Picture'),
              formatter: (value, key, item) => {
                if (value) {
                  return (
                    '<a href="' +
                    value +
                    '" target="_blank"><img src="' +
                    value +
                    '" style="height: auto; width: auto; max-height: 80px; max-width: 80px;" /></a>'
                  )
                }
              }
            },
            {
              key: 'country_id',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              formatter: (value, key, item) => {
                return window.countries.find(x => x.id === value).iso_code
              }
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true
            }
          ],
          extraActions: [
            {
              label: this.$gettext('Close'),
              type: 'button',
              icon: 'check',
              title: this.$gettext('Close an Incidence after treat it'),
              variant: 'dark',
              position: 'end',
              disabledCondition: item => {
                return item.status !== 'incidence'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to CLOSE the COD Payment Incidence?'))) {
                  Vue.http.put(appConfig.api.baseUrl + 'cod-payments/' + item.id + '/close').then(response => {
                    window.moovaAdmin.refreshTable('apiListTable')
                  })
                }
              }
            },
            {
              label: this.$gettext('Discard'),
              type: 'button',
              icon: 'trash-alt',
              title: this.$gettext('Discard a COD payment'),
              variant: 'danger',
              position: 'end',
              disabledCondition: item => {
                return item.status !== 'pending'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to DISCARD the COD Payment?'))) {
                  Vue.http.delete(appConfig.api.baseUrl + 'cod-payments/' + item.id + '/discard').then(response => {
                    window.moovaAdmin.refreshTable('apiListTable')
                  })
                }
              }
            }
          ],
          includes: ['user.roles', 'platform', 'transactions'],
          filters: ['id', 'user.name', 'user.email', 'status', 'updated_at']
        },
        'cod-transactions': {
          title: this.$gettext('COD Transactions'),
          endpoint: 'cod-transactions',
          noIdLink: true,
          countrizeById: 'country_id',
          asyncCsvReport: false,
          fields: [
            {
              key: 'id'
            },
            {
              key: 'user_id',
              label: this.$gettext('User'),
              formatter: (value, key, item) => {
                return new Vue({
                  ...UserCard,
                  parent: this,
                  propsData: {
                    user: item.user,
                    profilePicture: false,
                    withTitles: false,
                    withMap: false
                  }
                }).$mount().$el.outerHTML
              }
            },
            {
              key: 'lol',
              label: this.$gettext('Company'),
              formatter: (value, key, item) => {
                return new Vue({
                  ...CompanyCard,
                  parent: this,
                  propsData: {
                    company: item.shipping.company,
                    profilePicture: false,
                    withTitles: false,
                    withContact: false
                  }
                }).$mount().$el.outerHTML
              }
            },
            // {
            //   key: 'shipping_service_id'
            // },
            {
              key: 'shipping',
              label: this.$gettext('Shipping'),
              formatter: (value, key, item) => {
                return (
                  '<a href="/edit/shippings/' +
                  value.id +
                  '" target="_blank">' +
                  Vue.options.filters.uuid(value.id) +
                  '</a>'
                )
              }
            },
            {
              key: 'cod_payment_id',
              label: this.$gettext('COD Payment ID')
            },
            {
              key: 'amount',
              label: this.$gettext('Amount')
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              formatter: (value, key, item) => {
                let color = 'info'
                if (value === 'deposited') {
                  color = 'success'
                } else if (value !== 'collected') {
                  color = 'danger'
                }
                return (
                  '<span class="text-' +
                  color +
                  ' text-nowrap"><span style="display: inline-block; width:5px; height:5px; border-radius: 100px; vertical-align:middle;" class="bg-' +
                  color +
                  '"></span> ' +
                  value +
                  '</span>'
                )
              }
            },
            {
              key: 'country_id',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              formatter: (value, key, item) => {
                return window.countries.find(x => x.id === value).iso_code
              }
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true
            }
          ],
          extraActions: [
            {
              label: this.$gettext('Lost'),
              type: 'button',
              icon: 'ghost',
              title: this.$gettext('Mark COD transaction as lost (Stolen or just lost)'),
              variant: 'danger',
              position: 'end',
              disabledCondition: item => {
                return item.status !== 'collected'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to MARK AS STOLEN/LOST the COD transaction?'))) {
                  Vue.http.put(appConfig.api.baseUrl + 'cod-transactions/' + item.id + '/lost').then(response => {
                    window.moovaAdmin.refreshTable('apiListTable')
                  })
                }
              }
            },
            {
              label: this.$gettext('Deposited'),
              type: 'button',
              icon: 'check',
              title: this.$gettext('Mark COD transaction as RECEIVED'),
              variant: 'success',
              position: 'start',
              visibilityCondition: item => {
                return item.country.cod_provider === 'manual-pod'
              },
              disabledCondition: item => {
                return item.status !== 'collected'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to MARK AS RECEIVED the COD transaction?'))) {
                  Vue.http.put(appConfig.api.baseUrl + 'cod-transactions/' + item.id + '/paid').then(response => {
                    window.moovaAdmin.refreshTable('apiListTable')
                  })
                }
              }
            }
          ],
          includes: ['user.roles', 'service', 'shipping', 'payment', 'country'],
          filters: ['id', 'user.name', 'user.email', 'status', 'shipping.company_id', 'updated_at']
        },
        'accounting-notes': {
          title: this.$gettext('Accounting Notes'),
          endpoint: 'accounting-notes',
          countrize: 'country_iso_code',
          noIdLink: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'type',
              label: this.$gettext('Type'),
              sortable: true,
              formatter: (value, key, item) => {
                let color = value === 'credit' ? 'danger' : 'success'
                return '<span class="text-' + color + '">' + value + '</span>'
              }
            },
            {
              key: 'amount',
              label: this.$gettext('Amount'),
              formatter: (value, key, item) => {
                return value + ' ' + item.currency
              }
            },
            {
              key: 'reference',
              label: this.$gettext('Reference')
            },
            {
              key: 'account',
              label: this.$gettext('Account'),
              formatter: (value, key, item) => {
                if (value.model_type.indexOf('User') !== -1) {
                  return new Vue({
                    ...UserCard,
                    parent: this,
                    propsData: {
                      user: value.owner,
                      profilePicture: false,
                      withTitles: false,
                      withMap: false
                    }
                  }).$mount().$el.outerHTML
                } else {
                  return new Vue({
                    ...CompanyCard,
                    parent: this,
                    propsData: {
                      company: value.owner,
                      profilePicture: false,
                      withTitles: false,
                      withContact: false
                    }
                  }).$mount().$el.outerHTML
                }
              }
            },
            {
              key: 'applied_by',
              label: this.$gettext('Applied By'),
              sortable: true,
              formatter: (value, key, item) => {
                return new Vue({
                  ...UserCard,
                  parent: this,
                  propsData: {
                    user: value,
                    profilePicture: false,
                    withTitles: false,
                    withMap: false
                  }
                }).$mount().$el.outerHTML
              }
            },
            {
              key: 'imputation_date',
              label: this.$gettext('Imputation Date'),
              sortable: true,
              editable: false,
              required: true
            }
          ],
          includes: ['account.owner', 'applied_by', 'shipping'],
          filters: ['id', 'status']
        },
        'pending-accounting-notes': {
          title: this.$gettext('Pending Accounting Notes'),
          endpoint: 'accounting-notes',
          countrize: 'country_iso_code',
          noIdLink: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'type',
              label: this.$gettext('Type'),
              sortable: true,
              formatter: (value, key, item) => {
                let color = value === 'credit' ? 'danger' : 'success'
                let html = ''
                html += '<span class="text-' + color + '">' + value + '</span>'
                if (item.is_outsourcing) {
                  html += '<br><span class="badge badge-danger">3P</span>'
                }
                return html
              }
            },
            {
              key: 'amount',
              label: this.$gettext('Amount'),
              formatter: (value, key, item) => {
                return value + ' ' + item.currency
              }
            },
            {
              key: 'reference',
              label: this.$gettext('Reference')
            },
            {
              key: 'account',
              label: this.$gettext('Account'),
              formatter: (value, key, item) => {
                if (value.model_type.indexOf('User') !== -1) {
                  return new Vue({
                    ...UserCard,
                    parent: this,
                    propsData: {
                      user: value.owner,
                      profilePicture: false,
                      withTitles: false,
                      withMap: false
                    }
                  }).$mount().$el.outerHTML
                } else {
                  return new Vue({
                    ...CompanyCard,
                    parent: this,
                    propsData: {
                      company: value.owner,
                      profilePicture: false,
                      withTitles: false,
                      withContact: false
                    }
                  }).$mount().$el.outerHTML
                }
              }
            },
            {
              key: 'applied_by',
              label: this.$gettext('Applied By'),
              sortable: true,
              formatter: (value, key, item) => {
                return new Vue({
                  ...UserCard,
                  parent: this,
                  propsData: {
                    user: value,
                    profilePicture: false,
                    withTitles: false,
                    withMap: false
                  }
                }).$mount().$el.outerHTML
              }
            },
            {
              key: 'imputation_date',
              label: this.$gettext('Imputation Date'),
              sortable: true,
              editable: false,
              required: true
            }
          ],
          includes: ['account.owner', 'applied_by', 'shipping'],
          filters: ['id', 'status', 'is_outsourcing']
        },
        'personal-pending-accounting-notes': {
          title: this.$gettext('My Pending Accounting Notes'),
          endpoint: 'accounting-notes',
          countrize: 'country_iso_code',
          noIdLink: true,
          noActions: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'type',
              label: this.$gettext('Type'),
              sortable: true,
              formatter: (value, key, item) => {
                let color = value === 'credit' ? 'danger' : 'success'
                return '<span class="text-' + color + '">' + value + '</span>'
              }
            },
            {
              key: 'amount',
              label: this.$gettext('Amount'),
              formatter: (value, key, item) => {
                return value + ' ' + item.currency
              }
            },
            {
              key: 'reference',
              label: this.$gettext('Reference')
            },
            {
              key: 'account',
              label: this.$gettext('Account'),
              formatter: (value, key, item) => {
                if (value.model_type.indexOf('User') !== -1) {
                  return new Vue({
                    ...UserCard,
                    parent: this,
                    propsData: {
                      user: value.owner,
                      profilePicture: false,
                      withTitles: false,
                      withMap: false
                    }
                  }).$mount().$el.outerHTML
                } else {
                  return new Vue({
                    ...CompanyCard,
                    parent: this,
                    propsData: {
                      company: value.owner,
                      profilePicture: false,
                      withTitles: false,
                      withContact: false
                    }
                  }).$mount().$el.outerHTML
                }
              }
            },
            {
              key: 'imputation_date',
              label: this.$gettext('Imputation Date'),
              sortable: true,
              editable: false,
              required: true
            }
          ],
          includes: ['account.owner', 'shipping'],
          filters: []
        },
        countries: {
          title: this.$gettext('Countries'),
          endpoint: 'countries',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'iso_code',
              label: this.$gettext('ISO Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('ISO CODE 3166-1 alpha 2 (ie. AR)')
            },
            {
              key: 'phone_code',
              label: this.$gettext('Phone Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext("Country's Phone Code <strong>without plus (+) sign</strong>")
            },
            {
              key: 'currency',
              label: this.$gettext('Currency'),
              editable: true,
              required: true,
              description: this.$gettext('Currency (Main currency ISO CODE 4217. Ie. ARS)')
            },
            {
              key: 'vat_percentage',
              label: this.$gettext('VAT Percentage'),
              editable: true,
              description: this.$gettext('VAT Percentage for the country (ie. 21.00)')
            },
            {
              key: 'language',
              label: this.$gettext('Language'),
              editable: true,
              required: true,
              description: this.$gettext('Language Locale (ISO 15897 code, ie. es_AR or pt_BR)')
            },
            {
              key: 'timezone',
              label: this.$gettext('TimeZone'),
              editable: true,
              required: true,
              description: this.$gettext(
                'TimeZone for dates on the country. Should be one present at: <a href="https://www.php.net/manual/es/timezones.php" target="_blank">https://www.php.net/manual/es/timezones.php</a>'
              )
            },
            {
              key: 'cut_time',
              label: this.$gettext('Cut Hour'),
              editable: true,
              required: true,
              description: this.$gettext('Cut hour for same-day shippings'),
              type: 'time',
              step: 1
            },
            {
              key: 'close_hours_treshold',
              label: this.$gettext('Close Hours Threshold'),
              editable: true,
              type: 'integer',
              description: this.$gettext(
                'Number of hours the shop must remain open after creating a shipping to deliver the same day'
              )
            },
            {
              key: 'credit_threshold',
              label: this.$gettext('Credit Threshold'),
              editable: true,
              required: false,
              description: this.$gettext(
                'Credit (credit_limit - balance) the account must have to create new shippings'
              )
            },
            {
              key: 'cod_max_carrying_cash',
              label: this.$gettext('COD Max. Carrying Cash'),
              editable: true,
              required: false,
              description: this.$gettext('Max amount of cash the user can carry before being forced to deposit')
            },
            {
              key: 'individual_terms',
              label: this.$gettext('Individual Terms'),
              editable: true,
              description: this.$gettext('Current terms of use for independent couriers')
            },
            {
              key: 'companies_terms',
              label: this.$gettext('Companies Terms'),
              editable: true,
              description: this.$gettext('Current terms of use for couriers belonging to a company')
            },
            {
              key: 'couriers_contract_model',
              label: this.$gettext("Couriers' Contract Model"),
              editable: true,
              description: this.$gettext('If required in the country the contract the couriers need to sign to work')
            },
            {
              key: 'couriers_contract_days',
              label: this.$gettext("Couriers' Contract Days"),
              editable: true,
              type: 'integer',
              description: this.$gettext('Number of days the contract signed by the courier is valid')
            },
            {
              key: 'max_working_hours_allowed',
              label: this.$gettext('Max. Working Hours'),
              editable: true,
              type: 'integer',
              description: this.$gettext('Maximum number of hours a courier is allowed to be working')
            },
            {
              key: 'front_config',
              label: this.$gettext('Front Config'),
              editable: true,
              type: 'JSON'
            }
          ],
          filters: ['id', 'name', 'iso_code'],
          exclude: ['front_config']
        },
        warehouses: {
          title: this.$gettext('Warehouses'),
          endpoint: 'warehouses',
          allowNew: true,
          allowDelete: true,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'reference',
              label: this.$gettext('Reference'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Reference text to identify the warehouse')
            },
            {
              key: 'contact',
              label: this.$gettext('Contact'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Name of the contact in the warehouse')
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country ISO Code'),
              sortable: true,
              editable: false,
              required: true,
              description: this.$gettext('ISO Code pointing to countries, to which country this setting applies for'),
              type: 'select',
              options: 'countries',
              showKey: 'iso_code',
              showField: 'name',
              withSelect: true
            },
            {
              key: 'phone',
              label: this.$gettext('Phone'),
              editable: false,
              description: this.$gettext("Warehouse's phone")
            },
            {
              key: 'email',
              label: this.$gettext('Email'),
              editable: true,
              required: true,
              description: this.$gettext("Warehouse's email address")
            },
            {
              key: 'place_id',
              label: this.$gettext('Address'),
              editable: true,
              required: true,
              formatter: (value, key, item) => {
                return item.place.formatted_address
              }
            },
            {
              key: 'address_extra',
              label: this.$gettext('Address Extra'),
              editable: true,
              required: true,
              description: this.$gettext('Additional info about the address, like floor and apartment')
            },
            {
              key: 'schedule',
              label: this.$gettext('Schedule'),
              editable: true,
              required: true,
              description: this.$gettext('Warehouse schedule, is an array of days which has for each day the schedule'),
              formatter: (value, key, item) => {
                let readable = ''
                let getScheduleGroups = collection => {
                  let groups = {}
                  collection.forEach((schedule, i) => {
                    let hasShifts = false
                    if (schedule.businessHours) {
                      let scheduleSignature = ''
                      let groupSchedule = []
                      schedule.businessHours.forEach((shift, n) => {
                        let hasHours = shift.from && shift.from !== ''
                        if (hasHours) {
                          hasShifts = true
                          groupSchedule.push(shift)
                          scheduleSignature += shift.from + '-' + shift.to
                        }
                      })
                      if (hasShifts) {
                        if (groups[scheduleSignature]) {
                          groups[scheduleSignature].days.push(schedule.dayName)
                        } else {
                          groups[scheduleSignature] = {
                            days: [schedule.dayName],
                            schedule: groupSchedule
                          }
                        }
                      }
                    }
                  })
                  return Object.values(groups)
                }
                let scheduleGroups = getScheduleGroups(value)
                if (scheduleGroups.length !== 0) {
                  scheduleGroups.forEach((group, i) => {
                    readable += group.days.join(', ') + '\n'
                    group.schedule.forEach((shift, i) => {
                      readable += '\t' + '- From ' + shift.from + ' to ' + shift.to + '\n'
                    })
                  })
                }
                return readable.replace(/\n/g, '<br>').replace(/\t/g, '&nbsp;&nbsp;')
              }
            }
          ],
          filters: ['id', 'reference', 'contact', 'phone', 'email', 'place.formatted_address', 'country_iso_code'],
          includes: ['place']
        },
        'warehouse-slots': {
          title: this.$gettext('Warehouse Slots'),
          endpoint: 'warehouse-slots',
          allowNew: true,
          allowDelete: true,
          countrize: 'warehouse.country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'warehouse_id',
              label: this.$gettext('Warehouse'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Home of the slot'),
              type: 'select',
              options: 'warehousesById',
              showField: 'reference',
              withSelect: true,
              formatter: (value, key, item) => {
                return item.warehouse && item.warehouse.reference ? item.warehouse.reference : ''
              }
            },
            {
              key: 'code',
              label: this.$gettext('Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Code text or number to identify the slot')
            },
            {
              key: 'reference',
              label: this.$gettext('Reference'),
              sortable: true,
              editable: true,
              description: this.$gettext('Reference text to identify the slot')
            },
            {
              key: 'tour_id',
              label: this.$gettext('Tour'),
              editable: false,
              formatter: (value, key, item) => {
                return value ? '<a href="/edit/tours/' + value + '" target="_blank">#' + value + '</a>' : ''
              }
            }
          ],
          includes: ['warehouse'],
          filters: ['id', 'reference', 'code', 'warehouse.reference', 'warehouse.country_iso_code', 'tour_id']
        },
        companies: {
          title: this.$gettext('Companies'),
          endpoint: 'companies',
          allowNew: true,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'business_name',
              label: this.$gettext('Business Name'),
              sortable: true,
              editable: true,
              required: false
            },
            {
              key: 'tin',
              label: this.$gettext('TIN'),
              editable: true,
              required: true
            },
            {
              key: 'contact_name',
              label: this.$gettext('Contact Name'),
              editable: true,
              required: true
            },
            {
              key: 'email',
              label: this.$gettext('Email'),
              editable: true,
              required: true
            },
            {
              key: 'phone',
              label: this.$gettext('Phone'),
              editable: true,
              required: true
            },
            {
              key: 'address',
              label: this.$gettext('Address'),
              editable: true,
              required: true
            },
            {
              key: 'logo',
              label: this.$gettext('Logo'),
              editable: true
            },
            {
              key: 'relationship',
              label: this.$gettext('Relationship'),
              sortable: true,
              editable: true
            },
            {
              key: 'tags',
              label: this.$gettext('Tags'),
              editable: false,
              sortable: false
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country ISO Code'),
              editable: true,
              required: true,
              description: this.$gettext('Country ISO 3166-1 alpha 2 code (ie. AR or BR)')
            }
          ],
          filters: [
            'id',
            'name',
            'business_name',
            'tin',
            'contact_name',
            'email',
            'phone',
            'country_iso_code',
            'relationship',
            'tags.name'
          ],
          exclude: ['address', 'logo'],
          includes: ['tags', 'account']
        },
        'vehicle-capacity': {
          title: this.$gettext('Vehicle Capacity'),
          endpoint: 'sizes',
          shared: 'semi',
          includes: ['vehicle_type_capacity.vehicle_type']
        },
        users: {
          title: this.$gettext('Users'),
          endpoint: 'users',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'profile_picture',
              sortable: false,
              label: this.$gettext('Picture')
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true
            },
            {
              key: 'email',
              label: this.$gettext('Email'),
              sortable: true,
              editable: true
            },
            {
              key: 'phone',
              label: this.$gettext('Phone')
            },
            {
              key: 'company_id',
              sortable: true,
              label: this.$gettext('Company'),
              formatter: (value, key, item) => {
                return item.company ? item.company.name : ''
              }
            },
            {
              key: 'active',
              type: Boolean,
              editable: false,
              label: this.$gettext('Enabled')
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At'),
              sortable: true
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true
            }
          ],
          filters: ['id', 'name', 'email', 'phone', 'company_id', 'tags.name'],
          includes: ['tags', 'company.tags']
        },
        'disable-reasons': {
          title: this.$gettext('Disable User Reasons'),
          endpoint: 'disable-reasons',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              description: this.$gettext('Name for the reason'),
              required: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              editable: true,
              description: this.$gettext('Description for the reason'),
              required: true
            },
            {
              key: 'reversible',
              label: this.$gettext('Reversible'),
              editable: true,
              type: Boolean,
              description: this.$gettext('Whether a model disabled with this reason can be reenabled or not'),
              required: true
            },
            {
              key: 'model_type',
              label: this.$gettext('Entity'),
              editable: true,
              description: this.$gettext('Indicates to which kind of entity this reason applies'),
              type: 'select',
              options: [{ text: this.$gettext('User'), value: 'Api\\Users\\Models\\User' }],
              required: true
            }
          ],
          filters: ['id', 'name', 'model_type']
        },
        couriers: {
          title: this.isMoova ? this.$gettext('Moovers') : this.$gettext('Couriers'),
          endpoint: 'users',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'profile_picture',
              sortable: false,
              label: this.$gettext('Picture')
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true
            },
            {
              key: 'email',
              label: this.$gettext('Email'),
              sortable: true,
              editable: true
            },
            {
              key: 'phone',
              label: this.$gettext('Phone')
            },
            {
              key: 'company_id',
              sortable: true,
              label: this.$gettext('Company'),
              formatter: (value, key, item) => {
                return item.company ? item.company.name : ''
              }
            },
            {
              key: 'cod',
              label: this.$gettext('COD'),
              formatter: (value, key, item) => {
                let html = ''
                if (item.courier_available && item.courier_available.carrying_cash_amount) {
                  if (item.courier_available.skips_cash_control) {
                    html += new Vue({
                      ...Icon,
                      parent: this,
                      propsData: {
                        icon: 'exclamation-triangle',
                        className: 'text-warning',
                        title: this.$gettext('Skips Cash Control')
                      }
                    }).$mount().$el.outerHTML
                  }
                  html +=
                    ' ' +
                    Vue.options.filters.currency(item.courier_available.carrying_cash_amount, item.country_iso_code)
                }
                return html
              }
            },
            {
              key: 'active',
              label: this.$gettext('Active'),
              editable: false
            },
            {
              key: 'free',
              label: this.$gettext('Free'),
              editable: false
            }
          ],
          filters: ['id', 'name', 'email', 'phone', 'company_id', 'tags.name'],
          includes: ['courier_available.tracking', 'fixed_amounts.fixed_tour', 'tags', 'company.tags']
        },
        roles: {
          title: this.$gettext('Roles'),
          endpoint: 'roles',
          allowNew: true,
          shared: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'guard_name',
              label: this.$gettext('Guard Name'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Should normally be "api"')
            }
          ],
          filters: ['id', 'name', 'guard_name']
        },
        'mobile-apps': {
          title: this.$gettext('Mobile Apps'),
          endpoint: 'mobile-apps',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: true,
              editable: true,
              required: false
            },
            {
              key: 'meta',
              label: this.$gettext('Meta'),
              editable: true,
              type: 'JSON'
            }
          ],
          filters: ['id', 'name', 'description'],
          exclude: ['meta']
        },
        permissions: {
          title: this.$gettext('Permissions'),
          endpoint: 'permissions',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'guard_name',
              label: this.$gettext('Guard Name'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Should normally be "api"')
            }
          ],
          filters: ['id', 'name', 'guard_name']
        },
        'fixed-prices': {
          title: this.$gettext('Fixed Prices'),
          endpoint: 'fixed-amount-price-schedules',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'type',
              label: this.$gettext('Type'),
              sortable: true
            },
            {
              key: 'model_id',
              sortable: true,
              label: this.$gettext('Owner')
            },
            {
              key: 'from_date',
              sortable: true,
              label: this.$gettext('Start Date')
            },
            {
              key: 'to_date',
              sortable: true,
              label: this.$gettext('End Date')
            },
            {
              key: 'amount',
              label: this.$gettext('Amount')
            },
            {
              key: 'status',
              label: this.$gettext('Status')
            }
          ],
          includes: [
            'fixed_tour.tour.courier.courier_available.tracking',
            'fixed_tour.tour.account_transactions',
            'owner'
          ],
          filters: []
        },
        'credit-cards': {
          title: 'Credit Cards',
          endpoint: 'credit-cards',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country ISO Code'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Country 3 letter ISO code')
            },
            {
              key: 'active',
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('1 or 0')
            }
          ],
          filters: ['id', 'name', 'country_iso_code']
        },
        'fleet-rental-orders': {
          title: this.$gettext('Fleet Rental Orders'),
          endpoint: 'fleet-rental-orders',
          allowNew: true,
          countrize: 'company.country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'company_id',
              sortable: true,
              editable: true,
              required: true,
              label: this.$gettext('Company'),
              formatter: (value, key, item) => {
                return new Vue({
                  ...CompanyCard,
                  parent: this,
                  propsData: {
                    company: item.company,
                    profilePicture: false,
                    withTitles: false,
                    withContact: false
                  }
                }).$mount().$el.outerHTML
              }
            },
            {
              key: 'reference',
              label: this.$gettext('Reference'),
              sortable: false,
              editable: true,
              required: false,
              description: this.$gettext('Rental order text reference')
            },
            {
              key: 'agreed_price',
              label: this.$gettext('Agreed Price'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Original agreed price with the company which requests the rental order'),
              formatter: (value, key, item) => {
                return CurrencyFilter(value, item.currency)
              }
            },
            {
              key: 'price',
              label: this.$gettext('Price'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Price that the company ends up paying'),
              formatter: (value, key, item) => {
                return CurrencyFilter(value, item.currency)
              }
            },
            {
              key: 'currency',
              label: this.$gettext('Currency'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Currency for the rental order price')
            },
            {
              key: 'date_from',
              label: this.$gettext('From Date'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Date since the rental applies')
            },
            {
              key: 'date_to',
              label: this.$gettext('To Date'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Date until the rental applies')
            },
            {
              key: 'amount_requested',
              label: this.$gettext('Amount Requested'),
              sortable: false,
              editable: true,
              required: true,
              description: this.$gettext('Amount of vehicles requested')
            },
            {
              key: 'place_id',
              label: this.$gettext('Place ID'),
              sortable: false,
              editable: true,
              required: false,
              description: this.$gettext('Place ID, where the fleet must be')
            },
            {
              key: 'place',
              label: this.$gettext('Address'),
              sortable: false,
              editable: true,
              required: false,
              formatter: (value, key, item) => {
                return (
                  '<span class="shipping-address-listings" title="' +
                  (item.place ? item.place.formatted_address : '-') +
                  '">' +
                  (item.place ? item.place.formatted_address : '-') +
                  '</span>'
                )
              }
            },
            {
              key: 'instructions',
              label: this.$gettext('Instructions'),
              sortable: false,
              editable: true,
              required: false,
              description: this.$gettext('Additional information')
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              sortable: true,
              editable: true,
              required: false,
              description: this.$gettext('Current status [ pending, confirmed, finished, canceled ]')
            }
          ],
          extraActions: [
            {
              label: this.$gettext('Clone'),
              type: 'button',
              icon: ['far', 'copy'],
              title: this.$gettext('Clone this Order'),
              variant: 'dark',
              position: 'start',
              disabledCondition: item => {
                return false
              },
              action: (item, that = null) => {
                if (confirm(this.$gettext('Are you sure you want to CLONE this Fleet Rental Order?'))) {
                  Vue.http.post(appConfig.api.baseUrl + 'fleet-rental-orders/' + item.id + '/clone').then(response => {
                    setTimeout(() => {
                      that.$root.$emit('bv::refresh::table', 'apiListTable')
                    }, 100)
                  })
                }
              }
            }
          ],
          includes: [
            'company.country',
            'fleet_rental_order_vehicles.courier.account',
            'fleet_rental_order_vehicles.vehicle.vehicle_type',
            'fleet_rental_order_vehicles.place',
            'fleet_rental_order_vehicles.status_history',
            'place'
          ],
          filters: ['id', 'company_id', 'company.country_iso_code', 'reference'],
          exclude: ['place_id', 'currency']
        },
        'receiver-types': {
          title: this.$gettext('Receiver Types'),
          endpoint: 'receiver-types',
          allowNew: true,
          shared: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: false,
              editable: true
            },
            {
              key: 'order',
              label: this.$gettext('Order'),
              sortable: true,
              editable: true
            }
          ],
          filters: ['id', 'name', 'description']
        },
        'shipping-types': {
          title: this.$gettext('Shipping Types'),
          endpoint: 'shipping-types',
          allowNew: true,
          shared: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: false,
              editable: true
            }
          ],
          filters: ['id', 'name', 'description']
        },
        'shipping-status-reasons': {
          title: this.$gettext('Shipping Status Reasons'),
          endpoint: 'shipping-status-reasons',
          allowNew: true,
          shared: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'event',
              label: this.$gettext('Event'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Must be one of: "CONFIRMED", "PICKEDUP", "INTRANSIT", "DELIVERED"')
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: false,
              editable: true
            }
          ],
          filters: ['id', 'event', 'name', 'description']
        },
        'shipping-status-reason-settings': {
          title: this.$gettext('Shipping Status Reason Settings'),
          endpoint: 'shipping-status-reason-settings',
          allowNew: true,
          allowDelete: true,
          shared: false,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'shipping_status_reasons_id',
              label: this.$gettext('Shipping Status Reason'),
              sortable: true,
              editable: false,
              required: true,
              description: this.$gettext(
                'Foreign Key to shipping status reasons table which defines when this happens and why'
              ),
              type: 'select',
              options: 'shippingStatusReasons'
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country'),
              sortable: true,
              editable: false,
              required: true,
              description: this.$gettext('ISO Code pointing to countries, to which country this setting applies for'),
              type: 'select',
              options: 'countries',
              withSelect: true
            },
            {
              key: 'relationship',
              label: this.$gettext('Relationship'),
              sortable: false,
              editable: false,
              required: true,
              description: this.$gettext(
                'To which type of transaction this rule applies. Must be one of: <strong>client</strong> or <strong>provider</strong>'
              ),
              type: 'select',
              options: [
                { text: this.$gettext('Client'), value: 'client' },
                { text: this.$gettext('Provider'), value: 'provider' }
              ]
            },
            {
              key: 'min_tour_shippings',
              label: this.$gettext('Minimum Tour Shippings'),
              sortable: false,
              editable: true,
              required: true,
              description: this.$gettext(
                'In case of shippings belonging to a tour, from how many shippings this setting applies for, otherwise should be 0 (zero)'
              )
            },
            {
              key: 'percentage',
              label: this.$gettext('Percentage'),
              sortable: false,
              editable: true,
              required: true,
              description: this.$gettext(
                '0 to 100, which percentage of the total shipping price/commission should apply'
              )
            }
          ],
          filters: ['id', 'shipping_status_reasons_id', 'relationship', 'country_iso_code']
        },
        'event-types': {
          title: this.$gettext('Event Types'),
          endpoint: 'event-types',
          allowNew: true,
          shared: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            }
          ],
          filters: ['id', 'name']
        },
        events: {
          title: this.$gettext('Events'),
          endpoint: 'events',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'area_id',
              label: this.$gettext('Area'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'event_type_id',
              label: this.$gettext('Event Type'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'amount',
              label: this.$gettext('Amount'),
              sortable: false,
              editable: true,
              required: true
            },
            {
              key: 'amount_type',
              label: this.$gettext('Amount Type'),
              sortable: false,
              editable: true,
              required: true
            },
            {
              key: 'from_date',
              label: this.$gettext('From Date'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'to_date',
              label: this.$gettext('To Date'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'active',
              label: this.$gettext('Active'),
              sortable: false,
              editable: true,
              required: true
            }
          ],
          filters: ['id', 'area_id', 'event_type_id', 'amount', 'amount_type', 'from_date', 'to_date', 'active'],
          exclude: ['amount_type'],
          includes: ['area', 'event_type']
        },
        applications: {
          title: this.$gettext('Company Applications'),
          endpoint: 'applications',
          allowNew: true,
          allowDelete: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'key',
              label: this.$gettext('Key'),
              sortable: false,
              editable: false
            },
            {
              key: 'company_id',
              label: this.$gettext('Company ID'),
              sortable: true,
              editable: true,
              required: true,
              formatter: (value, key, item) => {
                return value
              }
            },
            {
              key: 'platform_id',
              label: this.$gettext('Platform ID'),
              sortable: true,
              editable: true
            },
            {
              key: 'active',
              label: this.$gettext('Active'),
              sortable: false,
              editable: true,
              required: true,
              description: this.$gettext('1 or 0')
            },
            {
              key: 'webhook_enabled',
              label: this.$gettext('Webhook Enabled'),
              sortable: false,
              editable: true,
              required: true,
              description: this.$gettext('1 or 0')
            },
            {
              key: 'webhook_method',
              label: this.$gettext('Webhook Method'),
              sortable: false,
              editable: true,
              description: this.$gettext('POST or GET')
            },
            {
              key: 'webhook_url',
              label: this.$gettext('Webhook URL'),
              sortable: true,
              editable: true,
              description: this.$gettext('Complete and valid URL')
            },
            {
              key: 'webhook_headers',
              label: this.$gettext('Webhook Headers'),
              sortable: false,
              editable: true,
              type: 'JSON',
              description: this.$gettext('Array of objects (one Key/Value pair per object)')
            }
          ],
          filters: ['id', 'name', 'key', 'company_id'],
          exclude: ['webhook_headers']
        },
        platforms: {
          title: this.$gettext('Platforms'),
          endpoint: 'platforms',
          allowNew: true,
          shared: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'slug',
              label: this.$gettext('Slug'),
              sortable: true,
              editable: true,
              required: false,
              description: this.$gettext('Must be ONLY one lowercase word')
            },
            {
              key: 'grant_type',
              label: this.$gettext('Grant Type'),
              sortable: true,
              editable: true,
              required: false,
              description: this.$gettext('Must be one valid OAuth lowercase word')
            },
            {
              key: 'oauth_url',
              label: this.$gettext('OAuth URL'),
              sortable: true,
              editable: true,
              required: false,
              description: this.$gettext('Usually something like: https://thirdpartywebsiteapi/oauth/token')
            },
            {
              key: 'client_id',
              label: this.$gettext('Client ID'),
              sortable: true,
              editable: true,
              required: false
            },
            {
              key: 'client_secret',
              label: this.$gettext('Client Secret'),
              sortable: true,
              editable: true,
              required: false
            },
            {
              key: 'redirect_uri',
              label: this.$gettext('Redirect URI'),
              sortable: true,
              editable: true,
              required: false
            },
            {
              key: 'active',
              label: this.$gettext('Active'),
              sortable: true,
              editable: true,
              type: Boolean,
              required: false
            }
          ],
          filters: ['id', 'name', 'slug']
        },
        sizes: {
          title: this.$gettext('Sizes'),
          endpoint: 'sizes',
          allowNew: true,
          shared: 'semi',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'reference',
              label: this.$gettext('Reference'),
              sortable: false,
              editable: true,
              description: this.$gettext('Real World™ reference objects to exemplify the size.')
            },
            {
              key: 'max_length',
              label: this.$gettext('Max. Length'),
              sortable: false,
              editable: true,
              description: this.$gettext('Max length allowed in centimeters.')
            },
            {
              key: 'max_width',
              label: this.$gettext('Max. Width'),
              sortable: false,
              editable: true,
              description: this.$gettext('Max width allowed in centimeters.')
            },
            {
              key: 'max_height',
              label: this.$gettext('Max. Height'),
              sortable: false,
              editable: true,
              description: this.$gettext('Max height allowed in centimeters.')
            },
            {
              key: 'max_weight',
              label: this.$gettext('Max. Weight'),
              sortable: false,
              editable: true,
              description: this.$gettext('Max weight allowed in grams.')
            }
          ],
          filters: ['id', 'name', 'reference']
        },
        'vehicle-types': {
          title: this.$gettext('Vehicle Types'),
          endpoint: 'vehicle-types',
          allowNew: true,
          shared: 'semi',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'requires_documentation',
              label: this.$gettext('Req. Documentation'),
              sortable: false,
              editable: true,
              required: false,
              type: Boolean,
              default: 1,
              description: this.$gettext('If the vehicle needs to have documentation up to date in order to be used'),
              formatter: (value, key, item) => {
                return value === 1 ? this.$gettext('YES') : this.$gettext('NO')
              }
            },
            {
              key: 'is_registrable',
              label: this.$gettext('Req. Plate'),
              sortable: false,
              editable: true,
              required: false,
              type: Boolean,
              default: 1,
              description: this.$gettext('If the vehicle requires an identification number'),
              formatter: (value, key, item) => {
                return value === 1 ? this.$gettext('YES') : this.$gettext('NO')
              }
            },
            {
              key: 'max_distance_allowed',
              label: this.$gettext('Max. Distance Allowed'),
              sortable: false,
              editable: true,
              description: this.$gettext('Max distance allowed to the vehicle type in meters. 1000 m = 1 km')
            },
            {
              key: 'max_weight_allowed',
              label: this.$gettext('Max. Weight Allowed'),
              sortable: false,
              editable: true,
              description: this.$gettext('Max weight allowed to the vehicle type in grams. 1000 g = 1 kg')
            },
            {
              key: 'max_volume_allowed',
              label: this.$gettext('Max. Volume Allowed'),
              sortable: false,
              editable: true,
              description: this.$gettext('Max volume allowed to the vehicle type in litres. 1000 l = 1 m<sup>3</sup>')
            }
          ],
          filters: ['id', 'name']
        },
        'notification-types': {
          title: this.$gettext('Notification Types'),
          endpoint: 'notification-types',
          allowNew: true,
          shared: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: false,
              editable: true,
              description: this.$gettext('Notification type brief description')
            },
            {
              key: 'media_default',
              label: this.$gettext('Media Default'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext(
                'Indicates which media will be used to send the notification as default. Must be one of: email, whatsapp, sms'
              )
            }
          ],
          filters: ['id', 'name', 'media_default']
        },
        quotes: {
          title: this.$gettext('Quotes'),
          endpoint: 'quotes',
          allowNew: true,
          allowDelete: true,
          allowBulk: true,
          countrize: 'country_iso_from',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true
            },
            {
              key: 'area_id_from',
              label: this.$gettext('Origin Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: true,
              description: this.$gettext('Origin Area for this Quote'),
              default: null
            },
            {
              key: 'area_id_to',
              label: this.$gettext('Destination Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: true,
              description: this.$gettext('Destination Area for this Quote'),
              default: null
            },
            {
              key: 'region_id_from',
              label: this.$gettext('Origin Region'),
              type: 'select',
              options: 'regions',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: false,
              description: this.$gettext('Origin Region this quote is for'),
              default: null
            },
            {
              key: 'region_id_to',
              label: this.$gettext('Destination Region'),
              type: 'select',
              options: 'regions',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: false,
              description: this.$gettext('Destination Region this quote is for'),
              default: null
            },
            {
              key: 'platform_id',
              label: this.$gettext('Platform'),
              sortable: true,
              editable: true
            },
            {
              key: 'size_id',
              label: this.$gettext('Size'),
              sortable: true,
              editable: true
            },
            {
              key: 'shipping_type_id',
              label: this.$gettext('Type'),
              sortable: true,
              editable: true
            },
            {
              key: 'company_id',
              label: this.$gettext('Companies Count'),
              sortable: false,
              editable: false,
              formatter: (value, key, item) => {
                return item.quote_companies.length > 0 ? item.quote_companies.length : 0
              }
            }
          ],
          filters: [
            'id',
            'name',
            'area_id_from',
            'area_id_to',
            'region_id_from',
            'region_id_to',
            'vehicle_type_id',
            'shipping_type_id',
            'company_id'
          ]
        },
        services: {
          title: this.$gettext('Services'),
          endpoint: 'services',
          allowNew: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              required: true,
              sortable: true,
              editable: true,
              description: this.$gettext('The name may only contain letters, numbers, and dashes.')
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              sortable: true,
              editable: true,
              description: this.$gettext('The human readable Service name.')
            }
          ],
          filters: ['id', 'name', 'description']
        },
        'quote-services': {
          title: this.$gettext('Service Quotes'),
          endpoint: 'quote-services',
          allowNew: true,
          allowDelete: true,
          // allowBulk: true,
          countrizeById: 'country_id',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'service_id',
              label: this.$gettext('Service'),
              required: true,
              sortable: true,
              editable: true,
              formatter: (value, key, item) => {
                return item.service.description || item.service.name
              }
            },
            {
              key: 'country_id',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              formatter: (value, key, item) => {
                return window.countries.find(x => x.id === value).iso_code
              }
            },
            {
              key: 'company_id',
              label: this.$gettext('Company'),
              formatter: (value, key, item) => {
                return item.company ? item.company.name : ''
              }
            },
            {
              key: 'currency',
              label: this.$gettext('Currency')
            },
            {
              key: 'price_formula',
              label: this.$gettext('Price Formula')
            },
            {
              key: 'courier_commission_formula',
              label: this.$gettext('Courier Commission Formula')
            },
            {
              key: 'min_price',
              label: this.$gettext('Min. Price')
            },
            {
              key: 'max_price',
              label: this.$gettext('Max. Price')
            }
          ],
          filters: ['id', 'name', 'service.description', 'company.name', 'country.iso_code'],
          includes: ['service', 'company', 'country']
        },
        'quote-providers': {
          title: this.$gettext('Provider Quotes'),
          endpoint: 'quote-providers',
          allowNew: true,
          allowDelete: true,
          allowBulk: true,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'reference',
              label: this.$gettext('Quote Name'),
              required: true,
              sortable: true,
              editable: true
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('ISO Code pointing to countries, to which country this Quote applies for'),
              type: 'select',
              options: 'countries',
              showKey: 'iso_code',
              showField: 'name',
              withSelect: true,
              default: null,
              disabledCondition: item => {
                return !!item.id
              }
            },
            {
              key: 'company_id',
              label: this.$gettext('Provider'),
              editable: true,
              required: true,
              componentEdit: (value, key, item) => {
                return {
                  is: CompanyAutocomplete,
                  props: {
                    required: true,
                    restrictTo: [['relationship', 'eq', 'provider']],
                    preQuery: item.company ? item.company.name : null
                  },
                  on: {
                    selected: result => {
                      if (result) {
                        item[key] = result.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                if (item.company) {
                  let html = `<strong class="badge" style="background-color: ${this.stringToColour(
                    ' ' + item.company.id + item.company.id + item.company.id + item.company.id + item.company.id
                  )};${this.textContrastColor(
                    this.stringToColour(
                      ' ' + item.company.id + item.company.id + item.company.id + item.company.id + item.company.id
                    )
                  )}">${item.company.name}</strong>`
                  return html
                }
              }
            },
            {
              key: 'area_id_from',
              label: this.$gettext('Origin Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: false,
              description: this.$gettext('Origin Area for this Quote'),
              default: null,
              disabledCondition: item => {
                return !!(item.id || item.region_id_from || item.region_id_to)
              }
            },
            {
              key: 'area_id_to',
              label: this.$gettext('Destination Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: false,
              description: this.$gettext('Destination Area for this Quote'),
              default: null,
              disabledCondition: item => {
                return !!(item.id || item.region_id_from || item.region_id_to)
              }
            },
            {
              key: 'region_id_from',
              label: this.$gettext('Origin Region'),
              type: 'select',
              options: 'regions',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: false,
              description: this.$gettext('Origin Region this quote is for'),
              default: null,
              disabledCondition: item => {
                return !!(item.id || item.area_id_from || item.area_id_to)
              }
            },
            {
              key: 'region_id_to',
              label: this.$gettext('Destination Region'),
              type: 'select',
              options: 'regions',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: false,
              description: this.$gettext('Destination Region this quote is for'),
              default: null,
              disabledCondition: item => {
                return !!(item.id || item.area_id_from || item.area_id_to)
              }
            },
            {
              key: 'size_id',
              label: this.$gettext('Size'),
              type: 'select',
              options: 'shippingSizes',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: false,
              required: false,
              description: this.$gettext('Shipping size this quote is for'),
              default: null
            },
            {
              key: 'formula',
              label: this.$gettext('Formula'),
              editable: true,
              required: true,
              description: this.$gettext("Must be a number (variables won't work)"),
              type: 'number',
              step: '0.01',
              componentEdit: (value, key, item) => {
                return {
                  is: FieldQuoteFormula,
                  props: {
                    required: true,
                    quote: item,
                    variables: ['{kgms}'],
                    fieldName: 'formula',
                    customValuesField: 'formula_params',
                    excludedVariables: ['kgms']
                  }
                }
              }
            },
            {
              key: 'formula_params',
              editable: true,
              hidden: true
            },
            {
              key: 'date_from',
              label: this.$gettext('From Date'),
              sortable: true,
              editable: true,
              type: 'datetime',
              formatter: (value, key, item) => {
                if (value) {
                  const tz = window.countries.find(x => x.iso_code === item.country_iso_code).front_config.i18n.tz
                  return window.moment.utc(value).tz(tz).format('L LT')
                }
              },
              description: this.$gettext('Date since the Quote applies'),
              default: null
            },
            {
              key: 'date_to',
              label: this.$gettext('To Date'),
              sortable: true,
              editable: true,
              type: 'datetime',
              formatter: (value, key, item) => {
                if (value) {
                  const tz = window.countries.find(x => x.iso_code === item.country_iso_code).front_config.i18n.tz
                  return window.moment.utc(value).tz(tz).format('L LT')
                }
              },
              description: this.$gettext('Date until the Quote applies'),
              default: null
            }
          ],
          filters: [
            'id',
            'reference',
            'company.name',
            'country_iso_code',
            'area_id_from',
            'area_id_to',
            'region_id_from',
            'region_id_to'
          ],
          includes: ['company', 'area_from', 'area_to'],
          exclude: ['formula_params']
        },
        'tour-generation-bot-settings': {
          title: this.$gettext('Tour Generation Bot Settings'),
          endpoint: 'tour-generation-bot-settings',
          allowNew: true,
          allowDelete: true,
          countrize: 'region.country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'region_id',
              label: this.$gettext('Region'),
              type: 'select',
              options: 'regions',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: true,
              description: this.$gettext('To which region does this settings applies to'),
              default: null
            },
            {
              key: 'time_from',
              label: this.$gettext('Time From'),
              type: 'time',
              editable: true,
              required: true,
              description: this.$gettext('Time from this settings applies. Format hh:mm:ss'),
              withSeconds: true
            },
            {
              key: 'time_to',
              label: this.$gettext('Time To'),
              type: 'time',
              editable: true,
              required: true,
              description: this.$gettext('Time to this settings applies. Format hh:mm:ss'),
              withSeconds: true
            },
            {
              key: 'proximity',
              label: this.$gettext('Proximity'),
              editable: true,
              description: this.$gettext('Proximity to add shippings at the beginning or end of the tour'),
              type: 'number',
              step: '0.1'
            },
            {
              key: 'tolerance',
              label: this.$gettext('Tolerance'),
              editable: true,
              description: this.$gettext('How much longer a tour could grow after a good one was found'),
              type: 'number',
              step: '0.1'
            },
            {
              key: 'stops_per_km',
              label: this.$gettext('Stops Per Km.'),
              editable: true,
              required: true,
              description: this.$gettext('Stops per Km.'),
              type: 'number',
              step: '0.1'
            },
            {
              key: 'min_shippings',
              label: this.$gettext('Min. Shippings'),
              editable: true,
              description: this.$gettext('Minimum number of shippings to generate a tour'),
              type: 'integer'
            },
            {
              key: 'max_shippings',
              label: this.$gettext('Max. Shippings'),
              editable: true,
              description: this.$gettext('Max number of shippings to generate a tour'),
              type: 'integer'
            },
            {
              key: 'estimated_completion_time',
              label: this.$gettext('Est. Completion Time'),
              editable: true,
              required: true,
              description: this.$gettext('Max estimated time for completion in hours'),
              type: 'number',
              step: '0.1'
            }
          ],
          filters: ['id', 'region.name', 'region.country_iso_code'],
          includes: ['region']
        },
        tours: {
          title: this.$gettext('Tours'),
          endpoint: 'tours',
          allowNew: false,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'label',
              label: this.$gettext('Label'),
              sortable: true
            },
            {
              key: 'optimized',
              label: this.$gettext('Opt.'),
              sortable: false
            },
            {
              key: 'courier_id',
              sortable: true,
              editable: true,
              label: this.$gettext('Courier')
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              sortable: true,
              editable: true
            },
            {
              key: 'stops',
              sortable: false,
              editable: false,
              label: this.$gettext('Visits')
            },
            {
              key: 'comment',
              label: this.$gettext('Comments'),
              sortable: false,
              editable: false
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At'),
              sortable: true,
              editable: false
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true,
              editable: false
            }
          ],
          filters: ['id', 'label', 'courier_id', 'status', 'created_at'],
          includes: [
            'shippings',
            'visits',
            'courier.courier_available.tracking',
            'fixed_amount_price_schedules.fixed_tour'
          ]
        },
        shippings: {
          title: this.$gettext('Shippings'),
          endpoint: 'shippings',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'company_id',
              sortable: false,
              label: this.$gettext('Posted by'),
              formatter: (value, key, item) => {
                return item.company ? item.company.name : ''
              }
            },
            {
              key: 'from_address',
              sortable: false,
              label: this.$gettext('Origin')
            },
            {
              key: 'last_known_place.formatted_address',
              sortable: false,
              label: this.$gettext('Last Known Place')
            },
            {
              key: 'to_address',
              sortable: false,
              label: this.$gettext('Destination')
            },
            {
              key: 'instructions',
              sortable: true,
              label: this.$gettext('Instruct.')
            },
            {
              key: 'scheduled_date',
              sortable: true,
              label: this.$gettext('Sched. At')
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              sortable: true
            },
            {
              key: 'courier',
              sortable: false,
              label: this.$gettext('Courier')
            },
            {
              key: 'courier_commission',
              sortable: false,
              label: this.$gettext('Comm.')
            },
            {
              key: 'size_id',
              sortable: false,
              label: this.$gettext('Size')
            },
            {
              key: 'shipping_type_id',
              sortable: false,
              label: this.$gettext('Type')
            },
            {
              key: 'sla_date',
              sortable: true,
              label: this.$gettext('SLA')
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At'),
              sortable: true
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true
            }
          ],
          filters: [
            'id',
            'from_place_id',
            'from_contact',
            'address_from.formatted_address',
            'from_email',
            'from_phone',
            'to_contact',
            'address_to.formatted_address',
            'to_email',
            'to_phone',
            'status',
            'scheduled_date',
            'created_at',
            'updated_at',
            'user_id',
            'company_id',
            'shipping_type_id',
            'tour_id',
            'sla_date'
          ],
          includes: [
            'courier.courier_available.tracking',
            'courier_company',
            'company',
            'user',
            'status_history',
            'pending_hooks',
            'tags',
            'threads',
            'previousShipping',
            'followingShippings',
            'items',
            'from_area',
            'to_area',
            'deliveryTimeRange',
            'last_known_place'
          ],
          highPerformanceIncludes: ['last_known_place']
        },
        'addresses-check': {
          title: this.$gettext('Shippings'),
          endpoint: 'shippings',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'to_meta',
              sortable: false,
              label: this.$gettext('Instruct.')
            },
            {
              key: 'meta.ai.addresses.to.aiAddress.original',
              sortable: false,
              label: this.$gettext('Original')
            },
            {
              key: 'meta.ai.addresses.to.aiAddress.clean',
              sortable: false,
              label: this.$gettext('Clean')
            },
            {
              key: 'meta.ai.addresses.to.aiAddress.place.formatted_address',
              sortable: false,
              label: this.$gettext('GPlaces')
            },
            {
              key: 'meta.ai.addresses.to.aiAddress.engine',
              sortable: false,
              label: this.$gettext('Engine')
            },
            {
              key: 'meta.ai.addresses.to.aiAddress.confidence',
              sortable: true,
              label: this.$gettext('Confidence')
            },
            {
              key: 'address_to.formatted_address',
              sortable: false,
              label: this.$gettext('Manual Selected')
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true
            }
          ],
          filters: ['id', 'updated_at', 'meta', 'to_country_iso_code'],
          includes: [],
          highPerformanceIncludes: []
        },
        'item-packs': {
          title: this.$gettext('Item Packs'),
          endpoint: 'item-packs',
          endpointDelete: 'item-packs/{id}/discard',
          allowNew: true,
          allowDelete: true,
          showIncludes: true,
          countrize: 'country_iso_code',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'reference',
              label: this.$gettext('Pack Reference'),
              sortable: true,
              editable: true,
              required: true
            },
            {
              key: 'reference_code',
              label: this.$gettext('Reference Code/SKU'),
              sortable: true,
              editable: true
            },
            {
              key: 'country_iso_code',
              label: this.$gettext('Country'),
              sortable: true
            },
            {
              key: 'created_by_id',
              label: this.$gettext('Created By'),
              sortable: true,
              formatter: (value, key, item) => {
                return '<a href="/edit/users/' + value + '" target="_blank">#' + value + '</a>'
              }
            },
            {
              key: 'quantity',
              label: this.$gettext('Items Quantity'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Quantity of items in the pack'),
              formatter: (value, key, item) => {
                return item.serial_numbers ? item.serial_numbers.length + '/' + value : '0/' + value
              },
              editCondition: item => {
                return item.status === 'DRAFT'
              }
            },
            {
              key: 'status',
              sortable: true,
              label: this.$gettext('Status')
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At'),
              sortable: true
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true
            },
            {
              key: 'serial_numbers',
              label: this.$gettext('Serial Numbers'),
              sortable: false,
              editable: true,
              type: Array
            }
          ],
          extraActions: [
            {
              label: this.$gettext('Pack Label'),
              type: 'button',
              icon: 'file-pdf',
              title: this.$gettext('Get Pack PDF label'),
              variant: 'dark',
              position: 'start',
              disabledCondition: item => {
                return false
              },
              action: item => {
                let data = {
                  url: appConfig.api.baseUrl + 'item-packs/' + item.id + '/label',
                  params: null,
                  type: 'application/pdf;charset=utf-8;',
                  filename: 'pack_' + item.id + '_label'
                }
                Vue.http.get(data.url, { params: data.params, responseType: 'blob' }).then(response => {
                  let blob = new Blob([response.body], { type: data.type })
                  if (navigator.msSaveBlob) {
                    // IE 10+
                    navigator.msSaveBlob(blob, data.filename)
                  } else {
                    let link = document.createElement('a')
                    if (link.download !== undefined) {
                      // feature detection
                      // Browsers that support HTML5 download attribute
                      let url = URL.createObjectURL(blob)
                      link.setAttribute('href', url)
                      link.setAttribute('download', data.filename)
                      link.style.visibility = 'hidden'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }
                  }
                })
              }
            }
          ],
          filters: ['id', 'reference', 'created_by_id', 'quantity', 'status'],
          exclude: ['serial_numbers']
        },
        items: {
          title: this.$gettext('Shipping Items'),
          endpoint: 'shipping-items',
          editInPlace: true,
          noIdLink: true,
          allowNew: false,
          showIncludes: false,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'shipping_id',
              label: this.$gettext('Shipping ID'),
              filter: 'uuid',
              sortable: true
            },
            {
              key: 'item_pack_id',
              label: this.$gettext('Item Pack ID'),
              filter: 'uuid',
              sortable: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              editable: true
            },
            {
              key: 'reference_code',
              label: this.$gettext('Ref. Code')
            },
            {
              key: 'width',
              label: this.$gettext('Width'),
              editable: true
            },
            {
              key: 'height',
              label: this.$gettext('Height'),
              editable: true
            },
            {
              key: 'length',
              label: this.$gettext('Length'),
              editable: true
            },
            {
              key: 'weight',
              label: this.$gettext('Weight'),
              editable: true
            },
            {
              key: 'size',
              label: this.$gettext('Item Size'),
              formatter: (value, key, item) => {
                return (
                  (item.width || 0) +
                  '×' +
                  (item.height || 0) +
                  '×' +
                  (item.length || 0) +
                  'cm - ' +
                  (item.weight || 0) +
                  'g'
                )
              }
            },
            {
              key: 'quantity',
              label: this.$gettext('Quantity')
            },
            {
              key: 'serial_number',
              label: this.$gettext('Serial Number'),
              type: Array,
              filter: 'arrayToList',
              editable: true
            }
          ],
          filters: [],
          includes: [],
          exclude: ['width', 'height', 'length', 'weight']
        },
        tags: {
          title: this.$gettext('Tags'),
          endpoint: 'tags',
          allowNew: true,
          allowDelete: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              sortable: true,
              editable: true,
              required: true,
              toLowerCase: true
            }
          ],
          filters: ['id', 'name']
        },
        'outsourcing-quotes': {
          title: this.$gettext('Outsourcing Quotes'),
          endpoint: 'outsourcing-quotes',
          allowNew: true,
          allowDelete: true,
          allowBulk: true,
          countrize: 'area_to.country',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'area_id_from',
              label: this.$gettext('Origin Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: true,
              description: this.$gettext('Origin Area this quote is for'),
              default: null
            },
            {
              key: 'area_id_to',
              label: this.$gettext('Destination Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: true,
              description: this.$gettext('Destination Area this quote is for'),
              default: null
            },
            {
              key: 'postal_code_to',
              label: this.$gettext('Destination Postal Code'),
              editable: true,
              description: this.$gettext(
                'Specific destination postal code this quote is for, is for highly specific quotes'
              ),
              type: 'text'
            },
            {
              key: 'company_id_customer',
              label: this.$gettext('Customer Company'),
              editable: true,
              componentEdit: (value, key, item) => {
                return {
                  is: CompanyAutocomplete,
                  props: {
                    required: false,
                    restrictTo: [['relationship', 'eq', 'client']],
                    preQuery: item.company?.name || null
                  },
                  on: {
                    selected: result => {
                      if (result) {
                        item[key] = result.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.company
                  ? new Vue({
                      ...CompanyCard,
                      parent: this,
                      propsData: {
                        company: item.company,
                        profilePicture: false,
                        withTitles: false,
                        withContact: false
                      }
                    }).$mount().$el.outerHTML
                  : ''
              }
            },
            {
              key: 'size_id',
              label: this.$gettext('Shipping Size'),
              type: 'select',
              options: 'shippingSizes',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              description: this.$gettext('Shipping size this quote is for'),
              default: null
            },
            {
              key: 'shipping_type_id',
              label: this.$gettext('Shipping Type'),
              type: 'select',
              options: 'shippingTypes',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              description: this.$gettext('Shipping type this quote is for'),
              default: null
            },
            {
              key: 'company_id_provider',
              label: this.$gettext('Provider Company'),
              editable: true,
              required: true,
              componentEdit: (value, key, item) => {
                return {
                  is: CompanyAutocomplete,
                  props: {
                    required: true,
                    restrictTo: [['relationship', 'eq', 'provider']],
                    preQuery: item.provider ? item.provider.name : null
                  },
                  on: {
                    selected: result => {
                      if (result) {
                        item[key] = result.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.provider
                  ? new Vue({
                      ...CompanyCard,
                      parent: this,
                      propsData: {
                        company: item.provider,
                        profilePicture: false,
                        withTitles: false,
                        withContact: false
                      }
                    }).$mount().$el.outerHTML
                  : ''
              }
            },
            {
              key: 'price',
              label: this.$gettext('Price'),
              editable: true,
              required: true,
              description: this.$gettext(
                'Price agreed with the supplier for shipments that match this quote. (This is the quote itself)'
              ),
              type: 'number',
              step: '0.1'
            },
            {
              key: 'applies_automatically',
              label: this.$gettext('Applies Automatically'),
              sortable: true,
              editable: true,
              type: Boolean,
              required: false,
              default: 0,
              formatter: (value, key, item) => {
                return value
                  ? `<strong class="text-success">${this.$gettext('YES')}</strong>`
                  : `<strong class="text-danger">${this.$gettext('NO')}</strong>`
              }
            },
            {
              key: 'distribution_factor',
              label: this.$gettext('Distribution Factor'),
              editable: true,
              description: this.$gettext(
                'From 0 to 127. In case of quote collisions, it is a number which will allow to decide to which provider gets assigned'
              ),
              type: 'integer'
            },
            {
              key: 'active',
              label: this.$gettext('Active'),
              sortable: true,
              editable: true,
              type: Boolean,
              required: false,
              default: 0,
              formatter: (value, key, item) => {
                return value
                  ? `<strong class="text-success">${this.$gettext('YES')}</strong>`
                  : `<strong class="text-danger">${this.$gettext('NO')}</strong>`
              }
            }
          ],
          filters: [
            'id',
            'area_from.name',
            'area_to.name',
            'postal_code_to',
            'provider.name',
            'provider.id',
            'size.name',
            'shipping_type.name',
            'company.name',
            'company.id',
            'price',
            'applies_automatically',
            'active'
          ],
          includes: ['area_from', 'area_to', 'company', 'provider']
        },
        'outsourcing-slas': {
          title: this.$gettext('Outsourcing SLAs'),
          endpoint: 'outsourcing-slas',
          allowNew: true,
          allowDelete: true,
          countrize: 'area_to.country',
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'area_id_from',
              label: this.$gettext('Origin Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: true,
              description: this.$gettext('Origin Area for this SLA'),
              default: null
            },
            {
              key: 'area_id_to',
              label: this.$gettext('Destination Area'),
              type: 'select',
              options: 'areas',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: true,
              sortable: true,
              required: true,
              description: this.$gettext('Destination Area for this SLA'),
              default: null
            },
            {
              key: 'company_id_provider',
              label: this.$gettext('Provider Company'),
              editable: true,
              componentEdit: (value, key, item) => {
                return {
                  is: CompanyAutocomplete,
                  props: {
                    required: true,
                    restrictTo: [['relationship', 'eq', 'provider']],
                    preQuery: item.provider ? item.provider.name : null
                  },
                  on: {
                    selected: result => {
                      if (result) {
                        item[key] = result.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.provider
                  ? new Vue({
                      ...CompanyCard,
                      parent: this,
                      propsData: {
                        company: item.provider,
                        profilePicture: false,
                        withTitles: false,
                        withContact: false
                      }
                    }).$mount().$el.outerHTML
                  : ''
              }
            },
            {
              key: 'sla_days',
              label: this.$gettext('SLA Days'),
              editable: true,
              required: true,
              description: this.$gettext('Additional days to add up to our SLA when assigning to this set'),
              type: 'number',
              step: '0.1'
            }
          ],
          filters: ['id', 'area_from.name', 'area_to.name', 'provider.name', 'provider.id'],
          includes: ['area_from', 'area_to', 'provider']
        },
        'outsourcing-model-mappings': {
          title: this.$gettext('Outsourcing Model Mappings'),
          endpoint: 'outsourcing-model-mappings',
          allowNew: true,
          allowDelete: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'platform_id',
              label: this.$gettext('Platform'),
              type: 'select',
              options: 'platforms3p',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              sortable: true,
              required: true,
              description: this.$gettext('To which platform the mapping is for'),
              default: null
            },
            {
              key: 'model_type',
              label: this.$gettext('Model Type'),
              type: 'select',
              options: 'outsourcingModelMappings',
              withSelect: true,
              showRaw: true,
              sortable: true,
              required: true,
              description: this.$gettext('The model type this map is for'),
              default: null
            },
            {
              key: 'model_id',
              label: this.$gettext('Model ID'),
              type: 'text',
              required: true,
              description: this.$gettext('The ID in our System')
            },
            {
              key: 'target_model_id',
              label: this.$gettext('Target Model ID'),
              type: 'text',
              editable: true,
              required: true,
              description: this.$gettext('The ID in the provider System')
            }
          ],
          filters: ['id', 'platform_id', 'model_type']
        },
        'outsourcing-customizations': {
          title: this.$gettext('Outsourcing Customizations'),
          endpoint: 'outsourcing/customizations',
          allowNew: true,
          allowDelete: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'name',
              label: this.$gettext('Name'),
              editable: true,
              description: this.$gettext('The name of the customization'),
              type: 'text',
              required: true
            },
            {
              key: 'description',
              label: this.$gettext('Description'),
              editable: true,
              description: this.$gettext('Customization description'),
              type: 'text'
            },
            {
              key: 'model_type',
              label: this.$gettext('Entity'),
              editable: true,
              description: this.$gettext('Indicates to which kind of entity this customization applies'),
              type: 'select',
              options: [{ text: this.$gettext('Company'), value: 'Api\\Companies\\Models\\Company' }],
              required: true,
              default: 'Api\\Companies\\Models\\Company'
            },
            {
              key: 'value_validations',
              label: this.$gettext('Value Validations'),
              editable: true,
              description: this.$gettext('Validations to apply to a value when setting a customization'),
              type: 'text'
            }
          ],
          filters: ['name', 'model_type']
        },
        'outsourcing-company-customizations': {
          title: this.$gettext('Outsourcing Company Customizations'),
          endpoint: 'outsourcing-company-customizations',
          editInPlace: true,
          allowNew: true,
          allowDelete: true,
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'company_id',
              label: this.$gettext('Company'),
              editable: false,
              required: false,
              description: this.$gettext('Which company is customizing behavior'),
              componentEdit: (value, key, item) => {
                return {
                  is: CompanyAutocomplete,
                  props: {
                    required: false,
                    restrictTo: [['relationship', 'eq', 'client']],
                    preQuery: item.company ? item.company.name : null,
                    disabled: !!item.id
                  },
                  on: {
                    selected: result => {
                      if (result) {
                        item[key] = result.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.company
                  ? new Vue({
                      ...CompanyCard,
                      parent: this,
                      propsData: {
                        company: item.company,
                        profilePicture: false,
                        withTitles: false,
                        withContact: false
                      }
                    }).$mount().$el.outerHTML
                  : ''
              }
            },
            {
              key: 'provider_id',
              label: this.$gettext('Provider'),
              editable: false,
              required: true,
              description: this.$gettext('To which provider this customization applies to'),
              componentEdit: (value, key, item) => {
                return {
                  is: CompanyAutocomplete,
                  props: {
                    required: true,
                    restrictTo: [['relationship', 'eq', 'provider']],
                    preQuery: item.provider ? item.provider.name : null,
                    disabled: !!item.id
                  },
                  on: {
                    selected: result => {
                      if (result) {
                        item[key] = result.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.provider
                  ? new Vue({
                      ...CompanyCard,
                      parent: this,
                      propsData: {
                        company: item.provider,
                        profilePicture: false,
                        withTitles: false,
                        withContact: false
                      }
                    }).$mount().$el.outerHTML
                  : ''
              }
            },
            {
              key: 'outsourcing_customization_id',
              label: this.$gettext('Outsourcing Customization'),
              type: 'select',
              options: 'outsourcingCustomizations',
              showKey: 'id',
              showField: 'name',
              withSelect: true,
              editable: false,
              sortable: true,
              required: true,
              description: this.$gettext('To which type of customization this record refers to'),
              default: null
            },
            {
              key: 'value',
              label: this.$gettext('Value'),
              editable: true,
              required: true,
              description: this.$gettext('Value for the customization'),
              type: 'text'
            }
          ],
          includes: ['company', 'provider', 'outsourcing_customization'],
          filters: ['company_id', 'company.name', 'provider_id', 'provider.name', 'outsourcing_customization.name']
        },
        'shippings-containers': {
          title: this.$gettext('Shippings Containers'),
          endpoint: 'shippings-containers',
          allowNew: true,
          allowDelete: false,
          editBottomSlot: () => import('@/components/ShippingsContainersEditBottomSlot'),
          fields: [
            {
              key: 'id',
              sortable: true
            },
            {
              key: 'from_place_id',
              label: this.$gettext('From'),
              editable: true,
              description: this.$gettext('Origin location'),
              required: true,
              componentEdit: (value, key, item) => {
                return {
                  is: AllInOneGeoAutocomplete,
                  props: {
                    required: true,
                    staticItems: true,
                    minCharacter: 4,
                    preQuery: item.address_from?.formatted_address || '',
                    disabled: !!item.id
                  },
                  on: {
                    selected: async result => {
                      await this.checkPlace(result)

                      if (this.theCheckedAddress) {
                        item[key] = this.theCheckedAddress.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.address_from.formatted_address
              }
            },
            {
              key: 'to_place_id',
              label: this.$gettext('To'),
              editable: true,
              description: this.$gettext('Target location'),
              required: true,
              componentEdit: (value, key, item) => {
                return {
                  is: AllInOneGeoAutocomplete,
                  props: {
                    required: true,
                    staticItems: true,
                    minCharacter: 4,
                    preQuery: item.address_to?.formatted_address || '',
                    disabled: !!item.id
                  },
                  on: {
                    selected: async result => {
                      await this.checkPlace(result)

                      if (this.theCheckedAddress) {
                        item[key] = this.theCheckedAddress.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.address_to.formatted_address
              }
            },
            {
              key: 'last_known_place_id',
              label: this.$gettext('Last Known Place'),
              editable: true,
              description: this.$gettext('Last checkpoint location'),
              componentEdit: (value, key, item) => {
                return {
                  is: AllInOneGeoAutocomplete,
                  props: {
                    staticItems: true,
                    minCharacter: 4,
                    preQuery: item.last_known_address?.formatted_address || '',
                    disabled: !!item.id
                  },
                  on: {
                    selected: async result => {
                      await this.checkPlace(result)

                      if (this.theCheckedAddress) {
                        item[key] = this.theCheckedAddress.id
                      } else {
                        item[key] = null
                      }
                    }
                  }
                }
              },
              formatter: (value, key, item) => {
                return item.last_known_address.formatted_address
              }
            },
            {
              key: 'shippings',
              label: this.$gettext('Shippings Qty.'),
              sortable: true,
              editable: false,
              hidden: true,
              formatter: (value, key, item) => {
                return value.length
              }
            },
            {
              key: 'weight',
              label: this.$gettext('Weight'),
              editable: true,
              description: this.$gettext('Weight in grams'),
              type: 'number',
              editCondition: item => {
                return item.status === 'DRAFT'
              }
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              sortable: true,
              editable: true,
              required: true,
              description: this.$gettext('Current status [ DRAFT, READY, INTRANSIT, DELIVERED, CANCELED ]'),
              type: 'select',
              options: ['DRAFT', 'READY', 'INTRANSIT', 'DELIVERED', 'CANCELED'],
              default: 'DRAFT',
              editCondition: item => {
                return false
              }
            },
            {
              key: 'pickup_shipping_id',
              label: this.$gettext('Pickup ID'),
              sortable: false,
              editable: false,
              hidden: true,
              formatter: (value, key, item) => {
                if (value) {
                  return (
                    '<a href="/edit/shippings/' +
                    value +
                    '" target="_blank">' +
                    Vue.options.filters.uuid(value) +
                    '</a>'
                  )
                }
              }
            },
            {
              key: 'applied_by_id',
              label: this.$gettext('Applied By'),
              sortable: true,
              editable: false,
              hidden: true,
              formatter: (value, key, item) => {
                return '<a href="/edit/users/' + value + '" target="_blank">' + item.applied_by.name + '</a>'
              }
            },
            {
              key: 'created_at',
              label: this.$gettext('Created At'),
              sortable: true
            },
            {
              key: 'updated_at',
              label: this.$gettext('Updated At'),
              sortable: true
            }
          ],
          extraActions: [
            {
              label: this.$gettext('Container Label'),
              type: 'button',
              icon: 'file-pdf',
              title: this.$gettext('Get Container PDF label'),
              variant: 'dark',
              position: 'start',
              disabledCondition: item => {
                return item.status === 'CANCELED'
              },
              action: item => {
                let data = {
                  url: appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/label',
                  params: null,
                  type: 'application/pdf;charset=utf-8;',
                  filename: 'shippings_container_' + item.id + '_label'
                }
                Vue.http.get(data.url, { params: data.params, responseType: 'blob' }).then(response => {
                  let blob = new Blob([response.body], { type: data.type })
                  if (navigator.msSaveBlob) {
                    // IE 10+
                    navigator.msSaveBlob(blob, data.filename)
                  } else {
                    let link = document.createElement('a')
                    if (link.download !== undefined) {
                      // feature detection
                      // Browsers that support HTML5 download attribute
                      let url = URL.createObjectURL(blob)
                      link.setAttribute('href', url)
                      link.setAttribute('download', data.filename)
                      link.style.visibility = 'hidden'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }
                  }
                })
              }
            },
            {
              label: this.$gettext('Container Manifest'),
              type: 'button',
              icon: 'file-pdf',
              title: this.$gettext('Get Container PDF Manifest'),
              variant: 'dark',
              position: 'start',
              disabledCondition: item => {
                return item.status === 'DRAFT' || item.status === 'CANCELED'
              },
              action: item => {
                Vue.http.get(appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/manifest').then(response => {
                  window.open(response.body.manifest_url)
                  this.$root.$emit('reloadItem')
                })
              }
            },
            {
              label: this.$gettext('Set as READY'),
              type: 'button',
              icon: 'check',
              title: this.$gettext('Set Container status as READY'),
              variant: 'success',
              position: 'start',
              disabledCondition: item => {
                return item.status !== 'DRAFT' || !item.shippings.length
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to SET AS READY this Container?'))) {
                  const payload = {
                    status: 'READY'
                  }
                  Vue.http
                    .patch(appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/status', payload)
                    .then(() => {
                      this.$root.$emit('reloadItem')
                    })
                }
              }
            },
            {
              label: this.$gettext('Set as CANCELED'),
              type: 'button',
              icon: 'trash-alt',
              title: this.$gettext('Set Container status as CANCELED'),
              variant: 'danger',
              position: 'start',
              disabledCondition: item => {
                return item.status === 'CANCELED'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to SET AS CANCELED this Container?'))) {
                  const payload = {
                    status: 'CANCELED'
                  }
                  Vue.http
                    .patch(appConfig.api.baseUrl + 'shippings-containers/' + item.id + '/status', payload)
                    .then(() => {
                      this.$root.$emit('reloadItem')
                    })
                }
              }
            }
          ],
          includes: ['appliedBy', 'address_from', 'address_to', 'last_known_address', 'shippings.shippingsContainer'],
          filters: ['id', 'status', 'pickup_shipping_id', 'created_at', 'updated_at', 'appliedBy.name']
        },
        shippingsInContainer: {
          title: this.$gettext('Shippings In Container'),
          endpoint: null,
          fields: [
            {
              key: 'shipping_id',
              label: this.$gettext('ID'),
              sortable: true,
              formatter: (value, key, item) => {
                if (item.id) {
                  return (
                    '<a href="/edit/shippings/' +
                    item.id +
                    '" target="_blank">' +
                    Vue.options.filters.uuid(item.id) +
                    '</a>'
                  )
                }
              }
            },
            {
              key: 'company_id',
              sortable: false,
              label: this.$gettext('Posted by'),
              formatter: (value, key, item) => {
                return item.company ? item.company.name : ''
              }
            },
            {
              key: 'status',
              label: this.$gettext('Status'),
              sortable: true
            },
            {
              key: 'sla_date',
              sortable: true,
              label: this.$gettext('SLA')
            }
          ],
          extraActions: [
            {
              label: this.$gettext('Remove'),
              type: 'button',
              icon: 'trash-alt',
              title: this.$gettext('Remove Shipping from Container'),
              variant: 'danger',
              position: 'end',
              disabledCondition: item => {
                return item.shippings_container[0].status !== 'DRAFT'
              },
              action: item => {
                if (confirm(this.$gettext('Are you sure you want to REMOVE the Shipping from this Container?'))) {
                  let payload = {
                    body: {
                      shippings: [item.id]
                    }
                  }
                  Vue.http
                    .delete(
                      appConfig.api.baseUrl + 'shippings-containers/' + item.shippings_container[0].id + '/shippings',
                      payload
                    )
                    .then(response => {
                      this.$root.$emit('reloadItem')
                    })
                }
              }
            }
          ],
          filters: [],
          includes: []
        }
      }
    }
  },
  mounted() {
    this.$appConfig.models = this.models
  }
}
